import { FC, memo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Icon, Tab, TabList, TabPanel, TabPanels, Tabs } from '@chakra-ui/react';
import { getQueryValue } from '@netiva/classifieds-common';

import { routes } from '@/lib/routes';
import { ApiKeys, ImportLogs } from './components';
import { RouterLink } from '@/components/global';
import { ExternalLinkIcon } from '@chakra-ui/icons';
import { useMatomoTagManager } from '@/hooks';

const tabs = ['keys', 'import-logs'] as const;

export const ApiPage: FC = memo(function ApiPage() {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const tab = getQueryValue(params.tab, 'keys') as 'keys' | 'import-logs';

  const { trackMatomoEvent } = useMatomoTagManager();

  const handleTabChange = (index: number) => {
    if (tabs[index]) {
      navigate(routes.api(tabs[index]));
    }
  };

  // matomo tracking
  const hasTrackedPageLoadRef = useRef(false);

  useEffect(() => {
    if (hasTrackedPageLoadRef.current) return;
    trackMatomoEvent({
      trackingCategory: 'Interaction_PI-Products',
      trackingAction: 'View_API-Integration',
    });
    hasTrackedPageLoadRef.current = true;
  }, [trackMatomoEvent]);

  return (
    <>
      <Tabs isLazy size="lg" index={tabs.indexOf(tab)} onChange={handleTabChange}>
        <TabList>
          <Tab>{t('api.tabs.apiKeys')}</Tab>
          <Tab>{t('api.tabs.importLogs')}</Tab>
          <Tab as={RouterLink} to={routes.apiDocs('tutorial')} target="_blank">
            {t('api.tabs.tutorial')}
            <Icon as={ExternalLinkIcon} ml={1} />
          </Tab>
          <Tab as={RouterLink} to={routes.apiDocs('docs')} target="_blank">
            {t('api.tabs.apiDocs')}
            <Icon as={ExternalLinkIcon} ml={1} />
          </Tab>
        </TabList>
        <TabPanels>
          <TabPanel px={0}>
            <ApiKeys />
          </TabPanel>
          <TabPanel px={0}>
            <ImportLogs />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </>
  );
});
